import { Insight } from '../../../components/office';
import { useFollowedSite } from '../../../contexts/SiteContext';
import { Notice } from '@verlo/shared/components/ui/notice';
import { BRAND } from '@verlo/shared/lib/brand';
import { useEffect, useMemo, useState } from 'react';
import { Layers3, ChevronLeft, ChevronRight, Sparkles, ArrowUpRight, ArrowDownRight,
  Loader2, Award, Crown, Coffee, Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { reportsIntelApi, menuApi } from '@verlo/shared/services/api';
import SeeMoreLink from '../../../components/reports/SeeMoreLink';
import { Button } from '@verlo/shared/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMoney = (v) => v == null ? '—' : `£${Math.round(v).toLocaleString('en-GB')}`;
const fmtPct = (v) => v == null ? '—' : `${v.toFixed(1)}%`;
const fmtPp = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}pp`;

const TIER_META = {
  entry:   { label: 'Entry',   color: '#94A3B8', bg: 'bg-paper',   text: 'text-ink-soft',   icon: Coffee },
  mid:     { label: 'Mid',     color: '#4F46E5', bg: 'bg-signal-info-tint',  text: 'text-signal-info',  icon: Award },
  premium: { label: 'Premium', color: BRAND.verlo, bg: 'bg-verlo/10', text: 'text-verlo',  icon: Crown },
};

const stepMonth = (year, month, delta) => {
  let m = month + delta;
  let y = year;
  while (m < 1)  { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return [y, m];
};

const SalesMixPage = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [siteId, setSiteId] = useFollowedSite('all');
  const [sites, setSites] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [categories, setCategories] = useState(null);
  const [movements, setMovements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const s = await reportsIntelApi.listSites();
        setSites(s.data.sites || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month, ...(siteId !== 'all' ? { site_id: siteId } : {}) };
      const [py, pm] = stepMonth(year, month, -1);
      const [s, t, c, m] = await Promise.all([
        reportsIntelApi.mixSummary(params),
        reportsIntelApi.mixTrend({ ...(siteId !== 'all' ? { site_id: siteId } : {}), months: 12, end_year: year, end_month: month }),
        reportsIntelApi.mixCategories(params),
        reportsIntelApi.mixMovements({
          ...(siteId !== 'all' ? { site_id: siteId } : {}),
          from_year: py, from_month: pm, to_year: year, to_month: month,
        }),
      ]);
      setSummary(s.data);
      setTrend(t.data);
      setCategories(c.data);
      setMovements(m.data);
    } catch {
      toast.error('Could not load sales mix');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, siteId]);


  const periodLabel = `${MONTH_LABELS[month - 1]} ${year}`;
  const hasPriorData = !!summary && summary.previous?.total_revenue > 0;

  // ---------- Trend chart geometry ----------
  const trendGeom = useMemo(() => {
    if (!trend?.series?.length) return null;
    const W = 900, H = 220, padX = 28, padY = 14;
    const n = trend.series.length;
    const stepX = (W - padX * 2) / Math.max(n - 1, 1);
    const points = trend.series.map((d, i) => {
      const isEmpty = (d.total_revenue || 0) < 1;
      return {
        x: padX + i * stepX,
        entry:   100 - d.share.entry, // stack inverted (entry at bottom = max y)
        mid:     100 - d.share.entry - d.share.mid,
        premium: 0,
        isEmpty,
        raw: d, i,
      };
    });
    const y = (v) => padY + (v / 100) * (H - padY * 2);
    // Build a path that flips to flat at the bottom (y=100) for empty months —
    // visually reads as a grey "no data" gutter instead of all-premium fill.
    const yFor = (p, lvl) => p.isEmpty ? y(100) : y(lvl);
    const areaEntry =
      `M ${padX},${y(100)} ` +
      points.map((p) => `L ${p.x.toFixed(1)},${yFor(p, p.entry).toFixed(1)}`).join(' ') +
      ` L ${(padX + (n - 1) * stepX).toFixed(1)},${y(100).toFixed(1)} Z`;
    const areaMid =
      points.map((p, i) => (i === 0 ? 'M' : 'L') + ` ${p.x.toFixed(1)},${yFor(p, p.entry).toFixed(1)}`).join(' ') +
      ' ' +
      points.slice().reverse().map((p) => `L ${p.x.toFixed(1)},${yFor(p, p.mid).toFixed(1)}`).join(' ') + ' Z';
    const areaPremium =
      points.map((p, i) => (i === 0 ? 'M' : 'L') + ` ${p.x.toFixed(1)},${yFor(p, p.mid).toFixed(1)}`).join(' ') +
      ' ' +
      points.slice().reverse().map((p) => `L ${p.x.toFixed(1)},${y(0).toFixed(1)}`).join(' ') + ' Z';
    return { W, H, padX, padY, points, areaEntry, areaMid, areaPremium, stepX, y };
  }, [trend]);

  return (
    <ReportsLayout>
      <div data-testid="sales-mix-page">
        {/* Header */}
        <div className="flex min-h-[36px] items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <p className="text-[14px] text-ink-soft max-w-2xl">
              Every menu item is auto-tiered into entry, mid and premium by price within its category. Track the share shift, spot the swing categories, and see which items are moving up or down.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SitePicker sites={sites} value={siteId} onChange={setSiteId} allowAll triggerClassName="h-9 w-[180px]" testId="mix-site-select" />
            <div className="flex items-center gap-1 bg-white border border-hairline rounded-md">
              <button onClick={() => { const [y, m] = stepMonth(year, month, -1); setYear(y); setMonth(m); }} className="h-9 w-9 flex items-center justify-center hover:bg-hover-tint" data-testid="mix-prev-month">
                <ChevronLeft size={14} />
              </button>
              <span className="px-3 text-[13px] font-semibold text-ink font-mono-display select-none" data-testid="mix-period-label">{periodLabel}</span>
              <button onClick={() => { const [y, m] = stepMonth(year, month, 1); setYear(y); setMonth(m); }} className="h-9 w-9 flex items-center justify-center hover:bg-hover-tint" data-testid="mix-next-month">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Direction banner */}
        {summary && summary.previous.total_revenue > 0 && (
          <DirectionBanner direction={summary.direction} delta={summary.delta_share.premium} />
        )}

        {/* Hero tiles — entry / mid / premium */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5" data-testid="mix-hero-tiles">
            {['entry', 'mid', 'premium'].map((k) => {
              const meta = TIER_META[k];
              const Icon = meta.icon;
              const share = summary.current.share[k];
              const rev = summary.current.revenue[k];
              const delta = summary.delta_share[k];
              const hasPrev = summary.previous.total_revenue > 0;
              return (
                <div key={k} className="bg-white border border-hairline/80 rounded-lg p-5" data-testid={`mix-tile-${k}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-md ${meta.bg}`}>
                      <Icon size={14} className={meta.text} />
                    </span>
                    <span className={`text-[11px] uppercase tracking-[0.15em] font-semibold ${meta.text}`}>{meta.label}</span>
                  </div>
                  <div className="font-mono-display text-3xl font-medium tabular-nums text-ink">{fmtPct(share)}</div>
                  <div className="text-[12px] text-ink-soft mt-0.5">of revenue · {fmtMoney(rev)}</div>
                  {hasPrev && (
                    <div className="mt-3 pt-3 border-t border-hairline flex items-center justify-between">
                      <span className={`text-[12.5px] font-mono-display font-semibold inline-flex items-center gap-1 ${delta > 0 ? 'text-signal-ok' : delta < 0 ? 'text-signal-stop' : 'text-ink-soft'}`}>
                        {delta > 0 && <ArrowUpRight size={11} />}
                        {delta < 0 && <ArrowDownRight size={11} />}
                        {fmtPp(delta)}
                      </span>
                      <span className="text-[12.5px] text-ink-faint">vs {MONTH_LABELS[summary.previous.month - 1]}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 12-month stacked area trend */}
        <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden mb-5" data-testid="mix-trend-chart">
          <div className="px-5 py-3 border-b border-hairline flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-semibold text-ink">12-month tier share trend</h3>
              <p className="text-[12.5px] text-ink-soft mt-0.5">Stacked share of revenue by tier. Premium moving up means guests are trading up</p>
            </div>
            <div className="flex items-center gap-3">
              {['entry', 'mid', 'premium'].map((k) => (
                <span key={k} className="inline-flex items-center gap-1 text-[12.5px] text-ink-soft">
                  <span className="w-2 h-2 rounded-md" style={{ background: TIER_META[k].color }} />
                  {TIER_META[k].label}
                </span>
              ))}
            </div>
          </div>
          <div className="p-4">
            {loading && <div className="h-[240px] flex items-center justify-center text-ink-faint text-sm">Loading…</div>}
            {!loading && trendGeom && (
              <svg viewBox={`0 0 ${trendGeom.W} ${trendGeom.H + 26}`} className="w-full" preserveAspectRatio="none">
                {[0, 25, 50, 75, 100].map((pct) => (
                  <g key={pct}>
                    <line x1={trendGeom.padX} x2={trendGeom.W - 8} y1={trendGeom.y(pct)} y2={trendGeom.y(pct)} stroke="#F1F5F9" strokeDasharray={pct === 0 || pct === 100 ? 0 : '2 3'} />
                    <text x={trendGeom.padX - 4} y={trendGeom.y(pct) + 3} fontSize="8" fill="#94A3B8" textAnchor="end">{100 - pct}%</text>
                  </g>
                ))}
                {/* Diagonal hatch pattern for "no data" months */}
                <defs>
                  <pattern id="mix-no-data" patternUnits="userSpaceOnUse" width="6" height="6">
                    <path d="M 0 6 L 6 0" stroke="#E2E8F0" strokeWidth="1" />
                  </pattern>
                </defs>
                {/* No-data background rects per empty month */}
                {trendGeom.points.map((p, i) => {
                  if (!p.isEmpty) return null;
                  const prevX = i > 0 ? trendGeom.points[i - 1].x : p.x;
                  const nextX = i < trendGeom.points.length - 1 ? trendGeom.points[i + 1].x : p.x;
                  const left = (prevX + p.x) / 2;
                  const right = (p.x + nextX) / 2;
                  return (
                    <rect key={`nd-${i}`} x={left} y={trendGeom.y(0)} width={right - left} height={trendGeom.H - trendGeom.padY * 2 + trendGeom.padY} fill="url(#mix-no-data)" />
                  );
                })}
                <path d={trendGeom.areaEntry} fill={TIER_META.entry.color} opacity={0.85} />
                <path d={trendGeom.areaMid} fill={TIER_META.mid.color} opacity={0.85} />
                <path d={trendGeom.areaPremium} fill={TIER_META.premium.color} opacity={0.95} />
                {trendGeom.points.map((p) => (
                  <text key={p.i} x={p.x} y={trendGeom.H + 18} textAnchor="middle" fontSize="9" fill={p.isEmpty ? '#CBD5E1' : '#94A3B8'}>{p.raw.label}</text>
                ))}
              </svg>
            )}
          </div>
        </div>

        {/* Categories + Movements + Insight */}
        <div className="grid lg:grid-cols-[1fr_360px] gap-4">
          {/* Per-category breakdown */}
          <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden" data-testid="mix-categories">
            <div className="px-5 py-3 border-b border-hairline">
              <h3 className="text-sm font-semibold text-ink">Mix by category</h3>
              <p className="text-[12.5px] text-ink-soft mt-0.5">Mix score 0 = all entry · 50 = all mid · 100 = all premium</p>
            </div>
            <div className="divide-y divide-hairline">
              {categories?.categories.length === 0 && (
                <div className="px-5 py-10 text-center text-ink-faint text-sm">No category sales in this period.</div>
              )}
              {categories?.categories.map((c) => (
                <div key={c.category} className="px-5 py-3 hover:bg-hover-tint/40" data-testid={`mix-cat-${c.category}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-1.5">
                    <span className="text-[13px] font-semibold text-ink capitalize">{c.category}</span>
                    <div className="flex items-center gap-3 text-[12.5px]">
                      <span className="text-ink-soft font-mono">{fmtMoney(c.total_revenue)}</span>
                      <span className={`font-mono-display font-semibold ${c.mix_score >= 60 ? 'text-verlo' : c.mix_score >= 40 ? 'text-signal-info' : 'text-ink-soft'}`}>Score {c.mix_score}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 h-2.5 rounded-full overflow-hidden bg-paper">
                    <div style={{ width: `${c.share.entry}%`, background: TIER_META.entry.color }} title={`Entry ${c.share.entry}%`} />
                    <div style={{ width: `${c.share.mid}%`,   background: TIER_META.mid.color }}   title={`Mid ${c.share.mid}%`} />
                    <div style={{ width: `${c.share.premium}%`, background: TIER_META.premium.color }} title={`Premium ${c.share.premium}%`} />
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[12.5px] text-ink-soft font-mono-display">
                    <span>E {c.share.entry}%</span>
                    <span>M {c.share.mid}%</span>
                    <span>P {c.share.premium}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column: movements + insight */}
          <div className="space-y-4">
            {hasPriorData && movements && (
              <MovementsCard movements={movements} />
            )}
            {!hasPriorData && (
              <Notice tone="info" word="First month" data-testid="mix-no-prior">
                No prior month to compare against. Trade-up / trade-down movers appear once you have two months of trade.
              </Notice>
            )}
            <Insight
              read={() => reportsIntelApi.mixInsight({ year, month, ...(siteId !== 'all' ? { site_id: siteId } : {}) })}
              purpose={`Where the mix is heading in ${MONTH_LABELS[month - 1]} ${year}, what is driving it, and one move for this week.`}
              testId="mix-insight"
            />
          </div>
        </div>
      </div>
    </ReportsLayout>
  );
};

// ---------- Sub-components ----------

const DirectionBanner = ({ direction, delta }) => {
  if (direction === 'stable') {
    return (
      <Notice tone="info" word="Stable" className="mb-5" data-testid="mix-direction-banner">
        Mix is holding steady: premium share moved {fmtPp(delta)} vs last month.
      </Notice>
    );
  }
  const isUp = direction === 'trade_up';
  return (
    <Notice tone={isUp ? 'ok' : 'stop'} word={isUp ? 'Trade-up' : 'Trade-down'} className="mb-5" data-testid="mix-direction-banner">
      Guests are trading {isUp ? 'up' : 'down'}: premium share moved <b>{fmtPp(delta)}</b> vs last month.
    </Notice>
  );
};

const MovementsCard = ({ movements }) => {
  const [tab, setTab] = useState('up');
  const [promotingId, setPromotingId] = useState(null);
  const [promotedIds, setPromotedIds] = useState(new Set());

  // Seed promotedIds from the live menu so the "Promoted on POS" label
  // survives a page reload after a previous promotion.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await menuApi.getAll();
        if (!alive) return;
        const set = new Set(
          (res.data || []).filter((m) => m.promoted).map((m) => m.id)
        );
        setPromotedIds(set);
      } catch { /* silent — worst case the label defaults to 'Promote on POS' */ }
    })();
    return () => { alive = false; };
  }, []);

  const list = tab === 'up' ? movements.trade_up : movements.trade_down;

  const handlePromote = async (m) => {
    setPromotingId(m.id);
    try {
      const res = await menuApi.setPromoted(m.id, !promotedIds.has(m.id));
      const nowPromoted = !!res.data.promoted;
      setPromotedIds((prev) => {
        const next = new Set(prev);
        if (nowPromoted) next.add(m.id); else next.delete(m.id);
        return next;
      });
      toast.success(nowPromoted ? `${m.name} promoted on POS` : `${m.name} unpinned from POS`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error('You need admin rights to promote items on POS');
      } else if (status === 404) {
        toast.error('Item not found. It may have been deleted from the menu.');
      } else {
        toast.error("Couldn't update POS promotion");
      }
    } finally {
      setPromotingId(null);
    }
  };

  return (
    <div className="bg-white border border-hairline/80 rounded-lg overflow-hidden" data-testid="mix-movements">
      <div className="px-4 py-3 border-b border-hairline">
        <h3 className="text-sm font-semibold text-ink">Top movers</h3>
        <p className="text-[12.5px] text-ink-soft mt-0.5">vs previous month · share-point swing</p>
      </div>
      <div className="px-4 pt-3">
        <div className="flex gap-1 bg-paper p-0.5 rounded-md">
          <button onClick={() => setTab('up')} className={`flex-1 px-2 py-1 text-[12.5px] font-semibold rounded ${tab === 'up' ? 'bg-white text-signal-ok ' : 'text-ink-soft'}`} data-testid="mix-mover-tab-up">
            Trade-up ({movements.trade_up.length})
          </button>
          <button onClick={() => setTab('down')} className={`flex-1 px-2 py-1 text-[12.5px] font-semibold rounded ${tab === 'down' ? 'bg-white text-signal-stop ' : 'text-ink-soft'}`} data-testid="mix-mover-tab-down">
            Trade-down ({movements.trade_down.length})
          </button>
        </div>
      </div>
      <div className="divide-y divide-hairline max-h-[280px] overflow-y-auto">
        {list.length === 0 && (
          <div className="px-4 py-6 text-center text-[12px] text-ink-faint">No {tab === 'up' ? 'trade-up' : 'trade-down'} movers in this window.</div>
        )}
        {list.map((m) => {
          const tier = TIER_META[m.tier] || TIER_META.mid;
          const isPromoted = promotedIds.has(m.id);
          const isPromoting = promotingId === m.id;
          return (
            <div key={m.id} className="px-4 py-2.5 hover:bg-hover-tint/40" data-testid={`mix-mover-${m.id}`}>
              <div className="flex items-baseline gap-2">
                <span className="text-[12px] font-semibold text-ink truncate flex-1">{m.name}</span>
                <span className={`text-[12.5px] font-mono-display font-semibold inline-flex items-center gap-0.5 ${m.delta_share >= 0 ? 'text-signal-ok' : 'text-signal-stop'}`}>
                  {m.delta_share >= 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                  {fmtPp(m.delta_share)}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[11px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${tier.bg} ${tier.text}`}>{tier.label}</span>
                <span className="text-[12.5px] text-ink-soft capitalize">{m.category}</span>
                <span className="text-[12.5px] text-ink-faint">·</span>
                <span className="text-[12.5px] font-mono-display text-ink-soft">{fmtMoney(m.from_revenue)} → {fmtMoney(m.to_revenue)}</span>
              </div>
              {/* Close-the-loop CTA — trade-up movers can be pinned to POS in one click */}
              {tab === 'up' && (
                <div className="mt-2 flex justify-end">
                  <Button
                    size="sm"
                    variant={isPromoted ? 'default' : 'outline'}
                    disabled={isPromoting}
                    onClick={() => handlePromote(m)}
                    className="h-7 px-2.5 text-[12.5px]"
                    data-testid={`mix-mover-promote-${m.id}`}
                  >
                    {isPromoting ? (
                      <Loader2 size={11} className="animate-spin mr-1" />
                    ) : (
                      <Megaphone size={11} className="mr-1" />
                    )}
                    {isPromoted ? 'Promoted on POS' : 'Promote on POS'}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SalesMixPage;
